import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  RefreshCw, Plus, Calendar, User, FileText, Play, Pause, 
  Trash2, Edit2, Clock, TrendingUp, AlertCircle 
} from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const RecurringInvoices = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: "",
    interval: "monthly",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    lines: [{ article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }],
    notes: "",
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, articlesRes] = await Promise.all([
        axios.get(`${API}/sales-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Filter recurring invoices and group by parent
      const recurring = invoicesRes.data.filter(inv => inv.is_recurring);
      
      // Group by original invoice (those without parent_invoice_id are templates)
      const templates = recurring.filter(inv => !inv.parent_invoice_id);
      
      setSubscriptions(templates);
      setCustomers(customersRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRecurring = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/recurring-invoices/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.message}`);
      if (response.data.invoice_numbers?.length > 0) {
        toast.info(`Nya fakturor: ${response.data.invoice_numbers.join(", ")}`);
      }
      fetchData();
    } catch (error) {
      toast.error("Kunde inte bearbeta återkommande fakturor");
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!formData.customer_id) {
      toast.error("Välj en kund");
      return;
    }
    if (formData.lines.length === 0 || !formData.lines[0].description) {
      toast.error("Lägg till minst en rad");
      return;
    }

    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      
      // Calculate totals
      const subtotal = formData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
      const vat_amount = formData.lines.reduce((sum, line) => 
        sum + (line.quantity * line.unit_price * (line.vat_rate / 100)), 0);
      const total = subtotal + vat_amount;

      const invoiceData = {
        customer_id: formData.customer_id,
        customer_name: customer?.name || "",
        invoice_date: formData.start_date,
        due_date: calculateDueDate(formData.start_date),
        lines: formData.lines.map(line => ({
          ...line,
          total: line.quantity * line.unit_price * (1 + line.vat_rate / 100)
        })),
        subtotal,
        vat_amount,
        total,
        notes: formData.notes,
        status: "draft",
        is_recurring: true,
        recurring_interval: formData.interval,
        recurring_end_date: formData.end_date || null,
        next_recurring_date: calculateNextDate(formData.start_date, formData.interval)
      };

      await axios.post(`${API}/sales-invoices`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Abonnemang skapat!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast.error("Kunde inte skapa abonnemang");
    }
  };

  const handleToggleActive = async (subscription) => {
    try {
      const newStatus = subscription.status === "draft" ? "sent" : "draft";
      await axios.put(`${API}/sales-invoices/${subscription.id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newStatus === "sent" ? "Abonnemang aktiverat" : "Abonnemang pausat");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera abonnemang");
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!confirm("Är du säker på att du vill ta bort detta abonnemang?")) return;
    
    try {
      await axios.delete(`${API}/sales-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abonnemang borttaget");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort abonnemang");
    }
  };

  const calculateDueDate = (startDate) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  const calculateNextDate = (startDate, interval) => {
    const date = new Date(startDate);
    switch (interval) {
      case "monthly":
        date.setMonth(date.getMonth() + 1);
        break;
      case "quarterly":
        date.setMonth(date.getMonth() + 3);
        break;
      case "yearly":
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        date.setMonth(date.getMonth() + 1);
    }
    return date.toISOString().split('T')[0];
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      interval: "monthly",
      start_date: new Date().toISOString().split('T')[0],
      end_date: "",
      lines: [{ article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }],
      notes: "",
      is_active: true
    });
    setEditingSubscription(null);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // If article selected, auto-fill description and price
    if (field === "article_id" && value) {
      const article = articles.find(a => a.id === value);
      if (article) {
        newLines[index].description = article.name;
        newLines[index].unit_price = article.price;
        newLines[index].vat_rate = article.vat_rate || 25;
      }
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      const newLines = formData.lines.filter((_, i) => i !== index);
      setFormData({ ...formData, lines: newLines });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getIntervalLabel = (interval) => {
    switch (interval) {
      case "monthly": return "Månadsvis";
      case "quarterly": return "Kvartalsvis";
      case "yearly": return "Årsvis";
      default: return interval;
    }
  };

  const getStatusBadge = (subscription) => {
    if (subscription.status === "sent" || subscription.status === "paid") {
      return <Badge className="bg-green-100 text-green-700">Aktiv</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">Pausad</Badge>;
  };

  // Calculate stats
  const activeSubscriptions = subscriptions.filter(s => s.status === "sent" || s.status === "paid");
  const monthlyRevenue = activeSubscriptions
    .filter(s => s.recurring_interval === "monthly")
    .reduce((sum, s) => sum + (s.total || 0), 0);
  const quarterlyRevenue = activeSubscriptions
    .filter(s => s.recurring_interval === "quarterly")
    .reduce((sum, s) => sum + (s.total || 0) / 3, 0);
  const yearlyRevenue = activeSubscriptions
    .filter(s => s.recurring_interval === "yearly")
    .reduce((sum, s) => sum + (s.total || 0) / 12, 0);
  const totalMRR = monthlyRevenue + quarterlyRevenue + yearlyRevenue;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recurring-invoices">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Abonnemangsfakturering</h1>
          <p className="text-slate-500 mt-1">Hantera återkommande fakturor och prenumerationer</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleProcessRecurring}
            disabled={processing}
            data-testid="process-recurring-btn"
          >
            {processing ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Generera förfallna
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-subscription-btn">
                <Plus className="mr-2 h-4 w-4" />
                Nytt abonnemang
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingSubscription ? "Redigera abonnemang" : "Skapa nytt abonnemang"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Customer Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kund *</Label>
                    <Select 
                      value={formData.customer_id} 
                      onValueChange={(value) => setFormData({...formData, customer_id: value})}
                    >
                      <SelectTrigger data-testid="subscription-customer">
                        <SelectValue placeholder="Välj kund" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Intervall *</Label>
                    <Select 
                      value={formData.interval} 
                      onValueChange={(value) => setFormData({...formData, interval: value})}
                    >
                      <SelectTrigger data-testid="subscription-interval">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Månadsvis</SelectItem>
                        <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                        <SelectItem value="yearly">Årsvis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Startdatum</Label>
                    <Input 
                      type="date" 
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      data-testid="subscription-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slutdatum (valfritt)</Label>
                    <Input 
                      type="date" 
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      data-testid="subscription-end-date"
                    />
                  </div>
                </div>

                {/* Invoice Lines */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Fakturarader</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addLine}>
                      <Plus size={14} className="mr-1" /> Lägg till rad
                    </Button>
                  </div>
                  
                  {formData.lines.map((line, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                      <div className="col-span-3">
                        <Label className="text-xs">Artikel</Label>
                        <Select 
                          value={line.article_id} 
                          onValueChange={(value) => updateLine(index, "article_id", value)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Välj..." />
                          </SelectTrigger>
                          <SelectContent>
                            {articles.map(article => (
                              <SelectItem key={article.id} value={article.id}>
                                {article.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Label className="text-xs">Beskrivning</Label>
                        <Input 
                          value={line.description}
                          onChange={(e) => updateLine(index, "description", e.target.value)}
                          placeholder="Beskrivning"
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-xs">Antal</Label>
                        <Input 
                          type="number"
                          value={line.quantity}
                          onChange={(e) => updateLine(index, "quantity", parseFloat(e.target.value) || 0)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Pris</Label>
                        <Input 
                          type="number"
                          value={line.unit_price}
                          onChange={(e) => updateLine(index, "unit_price", parseFloat(e.target.value) || 0)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-xs">Moms %</Label>
                        <Input 
                          type="number"
                          value={line.vat_rate}
                          onChange={(e) => updateLine(index, "vat_rate", parseFloat(e.target.value) || 0)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeLine(index)}
                          disabled={formData.lines.length === 1}
                        >
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Anteckningar</Label>
                  <Input 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Valfria anteckningar som visas på fakturan"
                  />
                </div>

                {/* Summary */}
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Summa per period:</span>
                    <span className="font-bold text-indigo-600">
                      {formatCurrency(formData.lines.reduce((sum, l) => 
                        sum + l.quantity * l.unit_price * (1 + l.vat_rate / 100), 0
                      ))}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Avbryt
                  </Button>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700" 
                    onClick={handleCreateSubscription}
                    data-testid="save-subscription-btn"
                  >
                    {editingSubscription ? "Uppdatera" : "Skapa abonnemang"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <RefreshCw className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Aktiva abonnemang</p>
                <p className="text-2xl font-bold">{activeSubscriptions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">MRR (Månadsintäkt)</p>
                <p className="text-2xl font-bold">{formatCurrency(totalMRR)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">ARR (Årsintäkt)</p>
                <p className="text-2xl font-bold">{formatCurrency(totalMRR * 12)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pausade</p>
                <p className="text-2xl font-bold">{subscriptions.length - activeSubscriptions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscriptions List */}
      <Card className="card-base" data-testid="subscriptions-list">
        <CardHeader>
          <CardTitle className="text-lg">Abonnemang</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {subscriptions.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {subscriptions.map((subscription) => (
                <div key={subscription.id} className="p-4 hover:bg-slate-50" data-testid={`subscription-${subscription.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-slate-100">
                        <User className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{subscription.customer_name || "Okänd kund"}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{getIntervalLabel(subscription.recurring_interval)}</span>
                          <span>•</span>
                          <span>{formatCurrency(subscription.total)}</span>
                          {subscription.next_recurring_date && (
                            <>
                              <span>•</span>
                              <span>Nästa: {subscription.next_recurring_date}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(subscription)}
                      
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleActive(subscription)}
                          title={subscription.status === "sent" ? "Pausa" : "Aktivera"}
                        >
                          {subscription.status === "sent" || subscription.status === "paid" ? (
                            <Pause size={16} className="text-amber-500" />
                          ) : (
                            <Play size={16} className="text-green-500" />
                          )}
                        </Button>
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteSubscription(subscription.id)}
                          title="Ta bort"
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Invoice lines preview */}
                  {subscription.lines && subscription.lines.length > 0 && (
                    <div className="mt-3 pl-14">
                      <div className="text-xs text-slate-400 mb-1">Innehåll:</div>
                      <div className="flex flex-wrap gap-2">
                        {subscription.lines.map((line, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {line.quantity}x {line.description}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <RefreshCw className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga abonnemang ännu</p>
              <p className="text-sm mt-1">Skapa ditt första abonnemang för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Så fungerar abonnemangsfakturering</p>
              <p className="text-sm text-blue-700 mt-1">
                När ett abonnemang är aktivt och nästa faktureringsdatum har passerat, 
                klicka på "Generera förfallna" för att automatiskt skapa nya fakturor. 
                Du kan också schemalägga detta som ett automatiskt cron-jobb.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecurringInvoices;
